import { Flex, Box, Button, Modal } from '@mantine/core';
import { IconChevronLeft, IconDots, IconKey, IconPlus, IconTrash } from '@tabler/icons-react';
import { GetRfidListResponse } from 'api/actions/get-rfid-list/get-rfid-list-response';
import { useApi } from 'api/api-context';
import Loader from 'components/Loader';
import MenuItem from 'components/MenuItem';
import ModalConfirmation from 'components/ModalConfirmation';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import panic from 'panic';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HOUSE_PAGE_PATH, RFID_ADD_PAGE_PATH, RFID_PAGE_PATH } from 'routes/paths';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import P1Medium from 'theme/components/Typography/P1Medium';
import P2Medium from 'theme/components/Typography/P2Medium';

type RfidArray = NonNullable<GetRfidListResponse['rfids']>;
type RfidItem = RfidArray[number];

/**
 * RFID chips page
 */
const Rfid: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const navigate = useNavigate();
  const [rfids, setRfids] = useState<RfidArray>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpened, setModalOpened] = useState(false);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RfidItem | null>(null);

  /**
   * Handle chip item click (open options modal)
   */
  const handleItemClick = (rfid: RfidItem) => {
    setSelectedItem(rfid);
    setModalOpened(true);
  };

  /**
   * Close options modal
   */
  const handleCloseModal = () => {
    setModalOpened(false);
  };

  /**
   * Open confirmation modal for unpair
   */
  const handleOpenConfirmationModal = () => {
    setModalOpened(false);
    setConfirmationModalOpened(true);
  };

  /**
   * Close confirmation modal
   */
  const handleCloseConfirmationModal = () => {
    setConfirmationModalOpened(false);
  };

  /**
   * Handle unpair click
   */
  const handleUnpairClick = () => {
    if (selectedItem?.id != null) {
      setLoading(true);
      const unpairRfid = getAction('UnpairRfid');
      unpairRfid({ parameters: { id: Number(selectedItem.id) } })
        .then(() => {
          setConfirmationModalOpened(false);
          loadRfids();
          navigate(RFID_PAGE_PATH.original);
        })
        .catch(panic)
        .finally(() => setLoading(false));
    }
  };

  /**
   * Load RFID chips
   */
  const loadRfids = () => {
    const getRfidList = getAction('GetRfidList');
    setLoading(true);
    getRfidList()
      .then(({ rfids }) => {
        setRfids(rfids || []);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRfids();
  }, [getAction]);

  return (
    <SimpleLayout>
      <Box>
        <Box p="1rem">
          <Flex direction="row" align="center">
            <Link
              to={HOUSE_PAGE_PATH.original}
              style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
            >
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back to my household')}</P2Medium>
            </Link>
          </Flex>
          <Flex gap="0.5rem">
            <IconKey size="2rem" color={theme.colors?.gray?.[12]} />
            <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('List of chips')}</H1SemiBold>
          </Flex>
        </Box>
        {loading ? (
          <Loader />
        ) : (
          <>
            <Box pb="8rem">
              {rfids.map((rfid) => (
                <Box
                  key={rfid.id}
                  onClick={() => handleItemClick(rfid)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleItemClick(rfid);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${_t('Chip options')} ${rfid.label || _t('RFID')}`}
                  style={{ cursor: 'pointer' }}
                >
                  <MenuItem to="" title={rfid.label ?? _t('RFID')} iconRight={IconDots} />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
      <Button
        variant="primary"
        size="lg"
        leftSection={<IconPlus />}
        style={{
          position: 'fixed',
          bottom: '2.5rem',
          right: '1rem',
          boxShadow: '0px 6px 12px 0px rgba(85, 31, 255, 0.30)',
        }}
        onClick={() => navigate(RFID_ADD_PAGE_PATH.original)}
      >
        {_t('Add new chip')}
      </Button>

      <Modal
        variant="secondary"
        opened={modalOpened}
        onClose={handleCloseModal}
        title={_t('Chip options')}
        transitionProps={{ transition: 'slide-up', duration: 400 }}
      >
        <Box>
          <Box>
            <Flex gap="0.5rem" onClick={handleOpenConfirmationModal} style={{ cursor: 'pointer' }}>
              <IconTrash />
              <P1Medium>{_t('Unpair chip')}</P1Medium>
            </Flex>
          </Box>
        </Box>
      </Modal>

      <ModalConfirmation
        requestModalOpened={confirmationModalOpened}
        handleCloseRequestModal={handleCloseConfirmationModal}
        handleSubmit={handleUnpairClick}
        title={_t('Do you really want to unpair this chip?')}
        cancelButtonText={_t('Cancel')}
        submitButtonText={_t('Yes, unpair')}
        loading={loading}
      />
    </SimpleLayout>
  );
};

export default Rfid;
